/* Static in-browser data seed for Riya Agro Singapore ERP.
   Port of backend/seed_data.py — no backend/API required. */

const uuid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

const nowIso = () => new Date().toISOString();
const days = (n) => new Date(Date.now() + n * 864e5).toISOString();
const r2 = (x) => Math.round(x * 100) / 100;

function buildDb() {
  const ts = nowIso();
  const db = {};

  const COLLECTIONS = [
    "entities", "counterparties", "banks", "brokers", "vessels", "commodities",
    "fx_rates", "futures_prices", "purchase_contracts", "sale_contracts",
    "contract_links", "freight_fixtures", "port_calls", "documents",
    "hedge_positions", "letters_of_credit", "accounts", "journal_entries",
    "posting_rules", "tasks", "audit_logs", "periods", "invoices",
  ];
  COLLECTIONS.forEach((c) => { db[c] = []; });

  const stamp = (d) => {
    if (!d.created_at) d.created_at = ts;
    if (!d.updated_at) d.updated_at = ts;
    return d;
  };
  const nid = uuid;

  // ---------------- Entities ----------------
  const e_sg = nid(), e_ri = nid(), e_gk = nid();
  const entities = [
    { id: e_sg, name: "Riya Agro Singapore Pte Ltd", code: "RASG", country: "Singapore", base_currency: "USD", local_currency: "SGD", type: "Trading", is_primary: true },
    { id: e_ri, name: "Riya Agro Industries Private Limited", code: "RAIPL", country: "India", base_currency: "INR", local_currency: "INR", type: "Parent/Group", is_primary: false },
    { id: e_gk, name: "Gokul Agro Resources Limited", code: "GARL", country: "India", base_currency: "INR", local_currency: "INR", type: "Parent/Group", is_primary: false },
  ].map(stamp);
  db.entities.push(...entities);

  // ---------------- Commodities ----------------
  db.commodities.push(...[
    { id: nid(), name: "CPO", full_name: "Crude Palm Oil", uom: "MT", futures_ref: "BMD CPO", category: "Edible Oil" },
    { id: nid(), name: "RBD Palm Olein", full_name: "RBD Palm Olein", uom: "MT", futures_ref: "BMD CPO", category: "Edible Oil" },
    { id: nid(), name: "Crude Soybean Oil", full_name: "Crude Degummed Soybean Oil", uom: "MT", futures_ref: "CBOT Soybean Oil", category: "Edible Oil" },
    { id: nid(), name: "Soybeans", full_name: "Soybeans", uom: "MT", futures_ref: "CBOT Soybeans", category: "Oilseed" },
    { id: nid(), name: "Sunflower Oil", full_name: "Crude Sunflower Oil", uom: "MT", futures_ref: "-", category: "Edible Oil" },
  ].map(stamp));

  // ---------------- FX rates ----------------
  db.fx_rates.push(...[
    { id: nid(), base: "USD", quote: "SGD", rate: 1.348, source: "Manual", as_of: ts },
    { id: nid(), base: "USD", quote: "INR", rate: 83.42, source: "Manual", as_of: ts },
    { id: nid(), base: "USD", quote: "MYR", rate: 4.71, source: "Manual", as_of: ts },
    { id: nid(), base: "USD", quote: "BRL", rate: 5.08, source: "Manual", as_of: ts },
    { id: nid(), base: "SGD", quote: "INR", rate: 61.88, source: "Manual", as_of: ts },
  ].map(stamp));

  // ---------------- Futures reference prices ----------------
  db.futures_prices.push(...[
    { id: nid(), exchange: "BMD", symbol: "CPO", contract_month: "Aug-26", price: 3980, currency: "MYR", as_of: ts },
    { id: nid(), exchange: "BMD", symbol: "CPO", contract_month: "Sep-26", price: 3925, currency: "MYR", as_of: ts },
    { id: nid(), exchange: "CBOT", symbol: "ZL", contract_month: "Jul-26", price: 47.85, currency: "USc/lb", as_of: ts },
    { id: nid(), exchange: "CBOT", symbol: "ZS", contract_month: "Jul-26", price: 1185.5, currency: "USc/bu", as_of: ts },
    { id: nid(), exchange: "MCX", symbol: "CPO", contract_month: "Jul-26", price: 962, currency: "INR/10kg", as_of: ts },
  ].map(stamp));

  // ---------------- Banks ----------------
  const banks = [
    { id: nid(), name: "DBS Bank Singapore", entity_id: e_sg, country: "Singapore", facility_limit: 120000000, swift: "DBSSSGSG" },
    { id: nid(), name: "Standard Chartered SG", entity_id: e_sg, country: "Singapore", facility_limit: 80000000, swift: "SCBLSGSG" },
    { id: nid(), name: "HSBC Singapore", entity_id: e_sg, country: "Singapore", facility_limit: 60000000, swift: "HSBCSGSG" },
    { id: nid(), name: "State Bank of India", entity_id: e_ri, country: "India", facility_limit: 4000000000, swift: "SBININBB" },
  ].map(stamp);
  const bank_ids = banks.map((b) => b.id);
  db.banks.push(...banks);

  // ---------------- Brokers ----------------
  db.brokers.push(...[
    { id: nid(), name: "Starsupreme Brokers", brokerage_rate: 0.25, country: "Singapore" },
    { id: nid(), name: "Kuala Lumpur Commodity Brokers", brokerage_rate: 0.30, country: "Malaysia" },
  ].map(stamp));

  // ---------------- Counterparties ----------------
  const cp = (name, type, country, rating, limit, group) =>
    stamp({ id: nid(), name, type, country, rating, credit_limit: limit, group, kyc_status: "Verified", related_party: group !== undefined });
  const suppliers = [
    cp("Sime Darby Plantation", "Supplier", "Malaysia", "AA", 40000000),
    cp("Golden Agri-Resources", "Supplier", "Indonesia", "A+", 35000000),
    cp("Wilmar International", "Supplier", "Singapore", "AA", 50000000),
    cp("Bunge Brazil", "Supplier", "Brazil", "A", 45000000),
    cp("ADM (Archer Daniels)", "Supplier", "USA", "AA-", 48000000),
  ];
  const buyers = [
    cp("Riya Agro Industries Pvt Ltd", "Buyer", "India", "A", 30000000, "Riya Group"),
    cp("Gokul Agro Resources Ltd", "Buyer", "India", "A-", 28000000, "Riya Group"),
    cp("Adani Wilmar", "Buyer", "India", "A+", 42000000),
    cp("COFCO International", "Buyer", "China", "AA-", 50000000),
    cp("Al Ghurair Foods", "Buyer", "UAE", "A", 25000000),
  ];
  const all_cp = [...suppliers, ...buyers];
  db.counterparties.push(...all_cp);
  const supp = Object.fromEntries(suppliers.map((c) => [c.name, c.id]));
  const buy = Object.fromEntries(buyers.map((c) => [c.name, c.id]));

  // ---------------- Vessels ----------------
  const vessel_defs = [
    ["MT Bunga Kelana", "Nominated", 45000, "Belawan", "Kandla", -12, 3, 180000, 55000, 22000, 18000, 95000],
    ["MT Stolt Sakura", "In Transit", 38000, "Santos", "Nhava Sheva", -25, -8, 210000, 62000, 26000, 21000, 110000],
    ["MT Pacific Emerald", "Loading", 42000, "Dumai", "Qingdao", -3, 8, 165000, 48000, 20000, 16000, 88000],
    ["MT Nord Star", "Discharge", 50000, "New Orleans", "Jebel Ali", -40, -20, 260000, 71000, 30000, 24000, 130000],
    ["MT Gemini Trader", "Settled", 35000, "Belawan", "Chennai", -60, -45, 150000, 44000, 18000, 15000, 78000],
    ["MT Ocean Harmony", "Laycan Agreed", 46000, "Paranagua", "Dalian", -18, 10, 195000, 58000, 24000, 20000, 102000],
  ];
  const vessels = [];
  for (const [name, status, dwt, load, disc, lstart, lend, fin, ins, port, survey, brok] of vessel_defs) {
    vessels.push(stamp({
      id: nid(), name, status, dwt, flag: "Singapore", owner: "Ocean Carriers Ltd",
      load_port: load, discharge_port: disc, laycan_start: days(lstart), laycan_end: days(lend),
      financing_cost: fin, insurance_cost: ins, port_charges: port, survey_fees: survey, brokerage: brok,
    }));
  }
  db.vessels.push(...vessels);
  const V = Object.fromEntries(vessels.map((v) => [v.name, v.id]));

  // ---------------- Purchase & Sale contracts + links ----------------
  const trades = [
    ["CPO", "Sime Darby Plantation", "Riya Agro Industries Pvt Ltd", 855.0, 892.0, 5000, "MT Bunga Kelana", "Malaysia/Belawan", "India/Kandla", "FOB", "CFR", "Aug-26", "Confirmed"],
    ["Crude Soybean Oil", "Bunge Brazil", "Adani Wilmar", 978.0, 1024.0, 6000, "MT Stolt Sakura", "Brazil/Santos", "India/Nhava Sheva", "FOB", "CIF", "Jul-26", "Confirmed"],
    ["RBD Palm Olein", "Golden Agri-Resources", "COFCO International", 905.0, 948.0, 8000, "MT Pacific Emerald", "Indonesia/Dumai", "China/Qingdao", "FOB", "CFR", "Aug-26", "Confirmed"],
    ["Crude Soybean Oil", "ADM (Archer Daniels)", "Al Ghurair Foods", 985.0, 1035.0, 7500, "MT Nord Star", "USA/New Orleans", "UAE/Jebel Ali", "FOB", "CFR", "Jul-26", "Confirmed"],
    ["CPO", "Wilmar International", "Gokul Agro Resources Ltd", 848.0, 878.0, 4500, "MT Gemini Trader", "Indonesia/Belawan", "India/Chennai", "FOB", "CFR", "Jun-26", "Confirmed"],
    ["Soybeans", "Bunge Brazil", "COFCO International", 468.0, 495.0, 12000, "MT Ocean Harmony", "Brazil/Paranagua", "China/Dalian", "FOB", "CFR", "Sep-26", "Confirmed"],
    ["Sunflower Oil", "ADM (Archer Daniels)", "Adani Wilmar", 1085.0, 1128.0, 3000, null, "Ukraine/Odessa", "India/Mundra", "CIF", "CIF", "Sep-26", "Draft"],
    ["RBD Palm Olein", "Sime Darby Plantation", "Al Ghurair Foods", 912.0, 902.0, 5500, null, "Malaysia/Pasir Gudang", "UAE/Jebel Ali", "FOB", "CFR", "Oct-26", "Cancelled"],
  ];
  const pcs = [], scs = [], links = [], lcs = [];
  let pc_seq = 1, sc_seq = 1;
  trades.forEach(([comm, sname, bname, bp, sp, qty, vname, origin, dest, bt, st, dm, status], i) => {
    const idx = i + 1;
    const vid = vname ? V[vname] : null;
    const vids = vid ? [vid] : [];
    const pc_ref = `AGP-2026-${String(pc_seq).padStart(2, "0")}`;
    const sc_ref = `AGS-2026-${String(sc_seq).padStart(2, "0")}`;
    const pc_id = nid(), sc_id = nid();
    pcs.push(stamp({
      id: pc_id, entity_id: e_sg, contract_ref: pc_ref, trade_no: String(idx),
      trade_type: "Forward", commodity: comm, grade: "Standard export grade",
      origin, counterparty_id: supp[sname], counterparty_name: sname,
      quantity: qty, tolerance_pct: 5, uom: "MT", pricing_basis: "TBF", futures_ref: comm, differential: 0,
      price: bp, currency: "USD", delivery_term: bt, load_port: origin.split("/").pop(), discharge_port: dest.split("/").pop(),
      payment_term: "LC at sight", incoterm: bt, delivery_month: dm,
      quality_terms: "SGS inspection at load port; FOSFA arbitration",
      status, version: 1, vessel_ids: vids, broker: "Starsupreme Brokers", brokerage_rate: 0.25,
    }));
    scs.push(stamp({
      id: sc_id, entity_id: e_sg, contract_ref: sc_ref, trade_no: String(idx),
      trade_type: "Forward", commodity: comm, grade: "Standard export grade",
      origin, counterparty_id: buy[bname], counterparty_name: bname,
      quantity: qty, tolerance_pct: 5, uom: "MT", pricing_basis: "Fixed", futures_ref: comm, differential: 0,
      price: sp, currency: "USD", delivery_term: st, load_port: origin.split("/").pop(), discharge_port: dest.split("/").pop(),
      payment_term: "LC at sight", incoterm: st, delivery_month: dm,
      destination_market: dest.split("/")[0], lc_required: true,
      quality_terms: "SGS inspection; GAFTA arbitration",
      status, version: 1, vessel_ids: vids, indicative_margin: r2((sp - bp) * qty),
    }));
    if (status !== "Cancelled" && idx !== 6) {
      links.push(stamp({ id: nid(), purchase_contract_id: pc_id, sale_contract_id: sc_id, allocated_qty: qty, coverage_pct: 100, note: "Back-to-back 100%" }));
    }
    if (status !== "Draft" && status !== "Cancelled") {
      lcs.push(stamp({
        id: nid(), lc_number: `2600111100${String(idx).padStart(3, "0")}`, entity_id: e_sg,
        bank_id: bank_ids[idx % 3], type: "Sight", amount: r2(sp * qty), currency: "USD",
        sale_contract_id: sc_id, purchase_contract_id: pc_id,
        issuing_bank: "State Bank of India", advising_bank: "DBS Bank Singapore",
        expiry_date: days(20 + idx * 6), latest_shipment: days(10 + idx * 5),
        presentation_status: idx % 2 ? "Documents Pending" : "Documents Presented",
        status: "Active", discrepancies: 0,
      }));
    }
    pc_seq += 1; sc_seq += 1;
  });

  // ---------------- Linking scenario demos (1:1, 1:Many, Many:1) ----------------
  const mk_pc = (ref, tno, comm, sname, qty, price, dm, dterm = "FOB", origin = "Malaysia/Belawan", dest = "India/Kandla") =>
    stamp({
      id: nid(), entity_id: e_sg, contract_ref: ref, trade_no: tno, trade_type: "Forward", commodity: comm,
      grade: "Standard export grade", origin, counterparty_id: supp[sname], counterparty_name: sname,
      quantity: qty, tolerance_pct: 5, uom: "MT", pricing_basis: "Fixed", futures_ref: comm, differential: 0,
      price, currency: "USD", delivery_term: dterm, load_port: origin.split("/").pop(), discharge_port: dest.split("/").pop(),
      payment_term: "LC at sight", incoterm: dterm, delivery_month: dm,
      quality_terms: "SGS inspection; FOSFA arbitration", status: "Confirmed", version: 1, vessel_ids: [],
      broker: "Starsupreme Brokers", brokerage_rate: 0.25,
    });
  const mk_sc = (ref, tno, comm, bname, qty, price, dm, dterm = "CFR", origin = "Malaysia/Belawan", dest = "India/Kandla") =>
    stamp({
      id: nid(), entity_id: e_sg, contract_ref: ref, trade_no: tno, trade_type: "Forward", commodity: comm,
      grade: "Standard export grade", origin, counterparty_id: buy[bname], counterparty_name: bname,
      quantity: qty, tolerance_pct: 5, uom: "MT", pricing_basis: "Fixed", futures_ref: comm, differential: 0,
      price, currency: "USD", delivery_term: dterm, load_port: origin.split("/").pop(), discharge_port: dest.split("/").pop(),
      payment_term: "LC at sight", incoterm: dterm, delivery_month: dm, destination_market: dest.split("/")[0],
      lc_required: true, quality_terms: "SGS inspection; GAFTA arbitration", status: "Confirmed",
      version: 1, vessel_ids: [], indicative_margin: r2(price * qty),
    });
  const mk_link = (pc, sc, alloc, note) =>
    stamp({ id: nid(), purchase_contract_id: pc.id, sale_contract_id: sc.id, allocated_qty: alloc, coverage_pct: Math.round((alloc / pc.quantity) * 1000) / 10, note, scenario: note });

  const a_pc = mk_pc("AGP-2026-31", "31", "CPO", "Sime Darby Plantation", 5000, 860.0, "Sep-26");
  const a_sc = mk_sc("AGS-2026-31", "31", "CPO", "Adani Wilmar", 5000, 903.0, "Sep-26");
  const b_pc = mk_pc("AGP-2026-32", "32", "RBD Palm Olein", "Golden Agri-Resources", 12000, 908.0, "Oct-26", "FOB", "Indonesia/Dumai", "China/Qingdao");
  const b_sc1 = mk_sc("AGS-2026-32A", "32", "RBD Palm Olein", "COFCO International", 6000, 951.0, "Oct-26", "CFR", "Indonesia/Dumai", "China/Qingdao");
  const b_sc2 = mk_sc("AGS-2026-32B", "32", "RBD Palm Olein", "Al Ghurair Foods", 4000, 955.0, "Oct-26", "CFR", "Indonesia/Dumai", "UAE/Jebel Ali");
  const b_sc3 = mk_sc("AGS-2026-32C", "32", "RBD Palm Olein", "Adani Wilmar", 2000, 958.0, "Oct-26", "CFR", "Indonesia/Dumai", "India/Mundra");
  const c_pc1 = mk_pc("AGP-2026-33A", "33", "Crude Soybean Oil", "Bunge Brazil", 6000, 980.0, "Nov-26", "FOB", "Brazil/Santos", "India/Nhava Sheva");
  const c_pc2 = mk_pc("AGP-2026-33B", "33", "Crude Soybean Oil", "ADM (Archer Daniels)", 4000, 986.0, "Nov-26", "FOB", "USA/New Orleans", "India/Nhava Sheva");
  const c_sc = mk_sc("AGS-2026-33", "33", "Crude Soybean Oil", "Riya Agro Industries Pvt Ltd", 10000, 1030.0, "Nov-26", "CFR", "Brazil/Santos", "India/Nhava Sheva");

  pcs.push(a_pc, b_pc, c_pc1, c_pc2);
  scs.push(a_sc, b_sc1, b_sc2, b_sc3, c_sc);
  links.push(
    mk_link(a_pc, a_sc, 5000, "One-to-One"),
    mk_link(b_pc, b_sc1, 6000, "One Purchase to Many Sales"),
    mk_link(b_pc, b_sc2, 4000, "One Purchase to Many Sales"),
    mk_link(b_pc, b_sc3, 2000, "One Purchase to Many Sales"),
    mk_link(c_pc1, c_sc, 6000, "Many Purchases to One Sale"),
    mk_link(c_pc2, c_sc, 4000, "Many Purchases to One Sale"),
  );

  db.purchase_contracts.push(...pcs);
  db.sale_contracts.push(...scs);
  db.contract_links.push(...links);
  db.letters_of_credit.push(...lcs);

  // ---------------- Freight fixtures + port calls per active vessel ----------------
  const fixtures = [], port_calls = [];
  for (const [name, status, dwt, load, disc, lstart, lend] of vessel_defs) {
    const vid = V[name];
    fixtures.push(stamp({
      id: nid(), vessel_id: vid, charter_type: "Voyage", charterer: "Riya Agro Singapore",
      freight_rate: r2(28 + (dwt % 7)), currency: "USD",
      laycan_start: days(lstart), laycan_end: days(lend),
      demurrage_rate: 18500, despatch_rate: 9250, load_port: load, discharge_port: disc,
      cp_form: "GENCON 94", status: "Fixed",
    }));
    const allowed = 96.0;
    const used = allowed + (status === "Discharge" || status === "Settled" ? 12 : -6);
    const dem = Math.max(0, (used - allowed)) / 24 * 18500;
    const desp = Math.max(0, (allowed - used)) / 24 * 9250;
    port_calls.push(stamp({
      id: nid(), vessel_id: vid, port: load, type: "Load",
      nor_tendered: days(lstart), laytime_allowed_hrs: allowed, laytime_used_hrs: used,
      demurrage_amount: r2(dem), despatch_amount: r2(desp),
    }));
  }
  db.freight_fixtures.push(...fixtures);
  db.port_calls.push(...port_calls);

  // ---------------- Hedge positions (F&O) ----------------
  const hedge_defs = [
    ["CPO", "BMD", "Aug-26", "Short", 200, 25, 3980, 3925, "Open", "MT Bunga Kelana", 0],
    ["Crude Soybean Oil", "CBOT", "Jul-26", "Short", 120, 27, 1024, 1008, "Open", "MT Stolt Sakura", 0],
    ["RBD Palm Olein", "BMD", "Aug-26", "Short", 320, 25, 948, 940, "Open", "MT Pacific Emerald", 0],
    ["Crude Soybean Oil", "CBOT", "Jul-26", "Short", 150, 27, 1035, 1052, "Closed", "MT Nord Star", 68850],
    ["CPO", "MCX", "Jun-26", "Short", 90, 10, 878, 872, "Closed", "MT Gemini Trader", 27000],
    ["Soybeans", "CBOT", "Sep-26", "Short", 240, 50, 495, 488, "Open", "MT Ocean Harmony", 0],
  ];
  db.hedge_positions.push(...hedge_defs.map(([comm, exch, month, side, lots, lot_size, entry, mtm, status, vname, realised]) =>
    stamp({
      id: nid(), entity_id: e_sg, commodity: comm, exchange: exch, contract_month: month, side, lots, lot_size,
      entry_price: entry, mtm_price: mtm, status, vessel_id: V[vname], realised_pnl: realised,
      margin_posted: r2(lots * lot_size * entry * 0.08), currency: "USD",
    })));

  // ---------------- Documents ----------------
  const doc_types = ["Bill of Lading", "Certificate of Origin", "Quality Certificate", "Charter Party"];
  for (const [name, status] of vessel_defs) {
    const vid = V[name];
    for (const dt of doc_types) {
      const dstatus = status === "Discharge" || status === "Settled" || status === "In Transit" ? "Received" : "Pending";
      db.documents.push(stamp({ id: nid(), vessel_id: vid, type: dt, status: dstatus, version: 1, reference: `${dt.slice(0, 2).toUpperCase()}-${vid.slice(0, 6)}` }));
    }
  }

  // ---------------- Tasks (project pipeline) ----------------
  const pipeline = ["Contract Confirmed", "Vessel Nominated", "Laycan Agreed", "Loading Complete",
    "B/L Issued", "Documents Negotiated", "Discharge Complete", "Invoice Settled", "Trade Closed"];
  const stage_map = { Nominated: 1, "Laycan Agreed": 2, Loading: 3, "In Transit": 5, Discharge: 6, Settled: 8 };
  const assignees = ["A. Menon (Ops)", "S. Tan (Trader)", "R. Iyer (Finance)", "K. Lim (Ops)"];
  vessel_defs.forEach(([name, status], idx) => {
    const vid = V[name];
    const stage = stage_map[status] || 0;
    pipeline.forEach((step, si) => {
      const tstatus = si <= stage ? "Done" : (si === stage + 1 ? "In Progress" : "Pending");
      db.tasks.push(stamp({
        id: nid(), vessel_id: vid, vessel_name: name, title: step, status: tstatus,
        assignee: assignees[si % 4], due_date: days(-20 + si * 5 + idx), order: si,
      }));
    });
  });

  // ---------------- Chart of Accounts ----------------
  const accounts = [
    ["1000", "Bank & Cash", "Asset"], ["1200", "Accounts Receivable", "Asset"],
    ["1300", "Inventory In-Transit", "Asset"], ["1400", "Derivative Margin Account", "Asset"],
    ["1500", "Intercompany Receivable", "Asset"],
    ["2100", "Accounts Payable", "Liability"], ["2200", "Trade Finance Payable", "Liability"],
    ["2300", "Intercompany Payable", "Liability"],
    ["3000", "Share Capital", "Equity"], ["3100", "Retained Earnings", "Equity"],
    ["4000", "Sales Revenue", "Revenue"], ["4100", "Hedge Gain", "Revenue"],
    ["5000", "Cost of Goods Sold", "Expense"], ["5100", "Freight Expense", "Expense"],
    ["5200", "Demurrage Expense", "Expense"], ["5300", "Financing Cost", "Expense"],
    ["5400", "Insurance & Survey", "Expense"], ["5500", "Brokerage Expense", "Expense"],
    ["5600", "Hedge Loss", "Expense"], ["5700", "FX Revaluation", "Expense"],
  ];
  db.accounts.push(...accounts.map(([code, name, type]) => stamp({ id: nid(), code, name, type, entity_id: e_sg })));

  // ---------------- Posting rules (event -> GL template) ----------------
  const rules = [
    ["Purchase Contract Confirmed", "1300", "Inventory In-Transit", "2100", "Accounts Payable", "Dr Inventory / Cr AP on purchase confirmation"],
    ["Sale Contract Confirmed", "1200", "Accounts Receivable", "4000", "Sales Revenue", "Dr AR / Cr Revenue on sale confirmation"],
    ["Provisional Invoice Raised", "1200", "Accounts Receivable", "4000", "Sales Revenue", "Dr AR / Cr Revenue on provisional invoice"],
    ["Bill of Lading Issued", "1300", "Inventory In-Transit", "1300", "Inventory In-Transit", "Inventory reclass on B/L"],
    ["Freight Booked", "5100", "Freight Expense", "2100", "Accounts Payable", "Dr Freight / Cr AP"],
    ["Demurrage Incurred", "5200", "Demurrage Expense", "2100", "Accounts Payable", "Dr Demurrage / Cr AP"],
    ["Price Fixed on Exchange", "1400", "Derivative Margin Account", "1000", "Bank & Cash", "Margin posting on price fixing"],
    ["LC Opened", "2200", "Trade Finance Payable", "1000", "Bank & Cash", "LC opening"],
    ["Final Invoice Settled", "1000", "Bank & Cash", "1200", "Accounts Receivable", "Cash receipt clears AR"],
  ];
  db.posting_rules.push(...rules.map(([event, dc, dn, cc, cn, desc]) =>
    stamp({ id: nid(), event, debit_account_code: dc, debit_account_name: dn, credit_account_code: cc, credit_account_name: cn, description: desc })));

  // ---------------- Journal entries from confirmed trades ----------------
  const jes = [];
  for (const pc of pcs) {
    if (pc.status === "Confirmed") {
      const val = pc.quantity * pc.price;
      jes.push(stamp({
        id: nid(), entity_id: e_sg, event: "Purchase Contract Confirmed",
        description: `Purchase ${pc.contract_ref} - ${pc.commodity}`,
        lines: [{ account_code: "1300", account_name: "Inventory In-Transit", debit: val, credit: 0 },
                { account_code: "2100", account_name: "Accounts Payable", debit: 0, credit: val }],
        source_type: "purchase_contract", source_id: pc.id, vessel_id: (pc.vessel_ids || [null])[0],
        currency: "USD", fx_rate: 1.0, entry_date: ts, status: "Posted",
      }));
    }
  }
  for (const sc of scs) {
    if (sc.status === "Confirmed") {
      const val = sc.quantity * sc.price;
      jes.push(stamp({
        id: nid(), entity_id: e_sg, event: "Sale Contract Confirmed",
        description: `Sale ${sc.contract_ref} - ${sc.commodity}`,
        lines: [{ account_code: "1200", account_name: "Accounts Receivable", debit: val, credit: 0 },
                { account_code: "4000", account_name: "Sales Revenue", debit: 0, credit: val }],
        source_type: "sale_contract", source_id: sc.id, vessel_id: (sc.vessel_ids || [null])[0],
        currency: "USD", fx_rate: 1.0, entry_date: ts, status: "Posted",
      }));
    }
  }
  for (const fx of fixtures) {
    const val = fx.freight_rate * 5000;
    jes.push(stamp({
      id: nid(), entity_id: e_sg, event: "Freight Booked",
      description: `Freight fixture ${fx.load_port}->${fx.discharge_port}`,
      lines: [{ account_code: "5100", account_name: "Freight Expense", debit: val, credit: 0 },
              { account_code: "2100", account_name: "Accounts Payable", debit: 0, credit: val }],
      source_type: "freight_fixture", source_id: fx.id, vessel_id: fx.vessel_id,
      currency: "USD", fx_rate: 1.0, entry_date: ts, status: "Posted",
    }));
  }
  jes.push(stamp({
    id: nid(), entity_id: e_sg, event: "Opening Balance", description: "Share capital injection",
    lines: [{ account_code: "1000", account_name: "Bank & Cash", debit: 60000000, credit: 0 },
            { account_code: "3000", account_name: "Share Capital", debit: 0, credit: 60000000 }],
    source_type: "manual", source_id: null, vessel_id: null, currency: "USD", fx_rate: 1.0, entry_date: ts, status: "Posted",
  }));
  db.journal_entries.push(...jes);

  // ---------------- Accounting periods ----------------
  const now = new Date();
  for (const off of [-3, -2, -1, 0, 1]) {
    const m = now.getMonth() + off;
    const y = now.getFullYear() + Math.floor(m / 12);
    const key = `${y}-${String((((m % 12) + 12) % 12) + 1).padStart(2, "0")}`;
    const locked = off <= -2;
    db.periods.push(stamp({ id: nid(), key, entity_id: e_sg, locked, status: locked ? "Locked" : "Open" }));
  }

  // ---------------- Pending manual journal (maker-checker demo) ----------------
  db.journal_entries.push(stamp({
    id: nid(), entity_id: e_sg, event: "Manual Adjustment",
    description: "Accrue Q3 office & desk operating expenses (SGD opex)",
    lines: [{ account_code: "5400", account_name: "Insurance & Survey", debit: 42000, credit: 0 },
            { account_code: "2100", account_name: "Accounts Payable", debit: 0, credit: 42000 }],
    source_type: "manual", source_id: null, vessel_id: null, currency: "USD", fx_rate: 1.0,
    entry_date: ts, status: "Pending Approval", entry_type: "Manual", maker: "R. Iyer (Finance)", checker: null,
  }));

  // ---------------- Invoices (provisional demo) ----------------
  scs.slice(0, 3).forEach((sc, i) => {
    const amt = sc.quantity * sc.price;
    db.invoices.push(stamp({
      id: nid(), invoice_number: `PINV-2026-${String(i + 1).padStart(3, "0")}`, type: "Provisional",
      status: "Open", sale_contract_id: sc.id, sale_contract_ref: sc.contract_ref,
      counterparty_name: sc.counterparty_name, commodity: sc.commodity,
      vessel_id: (sc.vessel_ids || [null])[0], entity_id: e_sg,
      amount: r2(amt * 0.9), currency: "USD", provisional_je_id: null, final_invoice_id: null, invoice_date: ts,
    }));
  });

  return db;
}

export const db = buildDb();

export function reseed() {
  const fresh = buildDb();
  Object.keys(db).forEach((k) => delete db[k]);
  Object.assign(db, fresh);
  return {
    ok: true,
    counts: {
      entities: db.entities.length, commodities: db.commodities.length, counterparties: db.counterparties.length,
      banks: db.banks.length, vessels: db.vessels.length, purchase_contracts: db.purchase_contracts.length,
      sale_contracts: db.sale_contracts.length, contract_links: db.contract_links.length, letters_of_credit: db.letters_of_credit.length,
      hedge_positions: db.hedge_positions.length, freight_fixtures: db.freight_fixtures.length, tasks: db.tasks.length,
      accounts: db.accounts.length, posting_rules: db.posting_rules.length, journal_entries: db.journal_entries.length,
    },
  };
}